/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import { useEffect, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { onTaskSuccess, TASK_NAMES } from '../../../common/utils/events/vital-stats-events';
import { useAnalytics } from '../../../controllers/analytics';
import { useFullPageSearchVitalStats } from '../vital-stats-context';

type useSearchResultsVitalStatsArgs = {
	edges: any[];
	searchSessionId: string;
};

export const useSearchResultsVitalStats = ({
	edges,
	searchSessionId,
}: useSearchResultsVitalStatsArgs) => {
	const { fireAnalyticsEvent } = useAnalytics();
	const vitalStats = useFullPageSearchVitalStats();
	const fullPageSearchTaskId = vitalStats.getUxTaskId();
	const networkRefs = vitalStats.getNetworkRefs();
	const onNetworkSuccess = vitalStats.onNetworkSuccess;

	const vitalStatsEventFiredRef = useRef(false);
	const networkVitalStatsEventFiredRef = useRef(false);
	const fullPageSearchTaskIdRefStable = useRef(fullPageSearchTaskId);
	const searchSessionIdRefStable = useRef(searchSessionId);

	// Keep refs up to date and reset vitalStatsEventFiredRef when task ID changes
	useEffect(() => {
		const taskIdChanged = fullPageSearchTaskId !== fullPageSearchTaskIdRefStable.current;

		if (taskIdChanged) {
			vitalStatsEventFiredRef.current = false;
			networkVitalStatsEventFiredRef.current = false;
		}

		fullPageSearchTaskIdRefStable.current = fullPageSearchTaskId;
		searchSessionIdRefStable.current = searchSessionId;
	}, [fullPageSearchTaskId, searchSessionId]);

	// Fire vital stats taskSuccess event when results are successfully rendered (UX)
	useEffect(() => {
		if (
			fullPageSearchTaskIdRefStable.current &&
			!vitalStatsEventFiredRef.current &&
			edges.length > 0 &&
			fg('rovo-search-vitalstats-fullpage-search-ux')
		) {
			vitalStatsEventFiredRef.current = true;
			fireAnalyticsEvent(
				onTaskSuccess({
					searchSessionId: searchSessionIdRefStable.current || '',
					taskId: fullPageSearchTaskIdRefStable.current,
					task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_RESULTS_UX,
				}),
			);
		}
		// Refs (fullPageSearchTaskIdRefStable, searchSessionIdRefStable, vitalStatsEventFiredRef)
		// are intentionally NOT in dependencies - we use refs to read latest values without
		// causing re-runs. fireAnalyticsEvent is stable and doesn't need to be included.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [edges.length, fullPageSearchTaskId]);

	// Fire network VitalStats taskSuccess event when results are received
	// Note: Network success fires for zero results too (edges.length === 0 is valid)
	useEffect(() => {
		if (
			networkRefs &&
			onNetworkSuccess &&
			!networkVitalStatsEventFiredRef.current &&
			fg('rovo-search-vitalstats-fullpage-search-network')
		) {
			const state = networkRefs.current.state;

			// Only fire if task is in 'started' state (prevents duplicates)
			if (state.state === 'started' && state.taskId) {
				networkVitalStatsEventFiredRef.current = true;
				onNetworkSuccess(edges.length);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [edges.length, fullPageSearchTaskId, networkRefs, onNetworkSuccess]);
};
